/**
 * Location Required Modal
 *
 * Prompts users to select a pickup location before adding items to cart.
 * Prevents adding items without location, ensuring single-location cart integrity.
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocations } from '@/hooks/useLocations';
import { useAvailableLocations } from '@/hooks/useCatalog';
import { useLocationStore } from '@/stores/useLocationStore';
import { CustomerLocationDto } from '@/types';
import { cn } from '@/lib/utils';

interface LocationRequiredModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLocationSelected: (location: CustomerLocationDto) => void;
    productName?: string;
    equipmentTypeId?: string;
    startDate?: string;
    endDate?: string;
    quantity?: number;
}

interface LocationOption {
    id: string;
    name: string;
    city?: string;
    state?: string;
    country?: string;
    availableQuantity?: number;
}

export const LocationRequiredModal = ({
    isOpen,
    onClose,
    onLocationSelected,
    productName,
    equipmentTypeId,
    startDate,
    endDate,
    quantity = 1,
}: LocationRequiredModalProps) => {
    // Use equipment-specific available locations when equipmentTypeId is provided
    const { data: availableLocations, isLoading: isLoadingAvailable } = useAvailableLocations(
        equipmentTypeId, startDate, endDate, quantity
    );

    // Fallback to all locations if no equipmentTypeId
    const { data: allLocations, isLoading: isLoadingAll } = useLocations();

    const isLoading = equipmentTypeId ? isLoadingAvailable : isLoadingAll;
    const { location: currentLocation } = useLocationStore();

    // Map available locations to a common shape for rendering
    const locations: LocationOption[] | undefined = equipmentTypeId
        ? availableLocations?.locations.map(loc => ({
            id: loc.locationId,
            name: loc.locationName,
            city: loc.city,
            state: loc.state,
            country: loc.country,
            availableQuantity: loc.availableQuantity,
        }))
        : allLocations?.map(loc => ({
            id: loc.id,
            name: loc.name,
            city: loc.city,
            state: loc.state,
            country: loc.country,
        }));

    const resolveSelectedLocation = (location: LocationOption): CustomerLocationDto => {
        const fullLocation = allLocations?.find(candidate => candidate.id === location.id);

        if (fullLocation) {
            return fullLocation;
        }

        return {
            id: location.id,
            name: location.name,
            city: location.city,
            state: location.state,
            country: location.country,
        } as CustomerLocationDto;
    };

    const hasNoLocations = !isLoading && (!locations || locations.length === 0);

    const handleLocationSelect = (location: LocationOption) => {
        onLocationSelected(resolveSelectedLocation(location));
        onClose();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                {/* Close Button */}
                                <button
                                    onClick={onClose}
                                    aria-label="Close dialog"
                                    className="absolute top-4 right-4 text-muted-foreground/70 hover:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full p-1"
                                >
                                    <X className="h-5 w-5" />
                                </button>

                                {/* Icon */}
                                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100">
                                    <MapPin className="h-6 w-6 text-primary-600" />
                                </div>

                                {/* Title */}
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-semibold text-center text-foreground mb-2"
                                >
                                    Choose a pickup location
                                </Dialog.Title>

                                {/* Description */}
                                <p className="text-sm text-muted-foreground text-center mb-6">
                                    {productName ? (
                                        <>
                                            Choose where you&apos;d like to collect and return{' '}
                                            <span className="font-medium">{productName}</span>. We&apos;ll only show
                                            locations where it&apos;s currently available.
                                        </>
                                    ) : (
                                        <>
                                            Choose your pickup location before adding this item.
                                            Availability can vary by location.
                                        </>
                                    )}
                                </p>

                                {/* Location List */}
                                <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                                    {isLoading ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            Checking available pickup locations...
                                        </div>
                                    ) : hasNoLocations ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <p className="font-medium">No pickup locations available</p>
                                            <p className="text-sm mt-1">
                                                This item is not currently available
                                                {startDate && endDate
                                                    ? ' for those dates at any pickup location.'
                                                    : ' at any pickup location.'}
                                            </p>
                                        </div>
                                    ) : (
                                        locations?.map((location) => (
                                            <button
                                                key={location.id}
                                                onClick={() => handleLocationSelect(location)}
                                                className={cn(
                                                    "w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
                                                    currentLocation?.id === location.id
                                                        ? "border-primary-500 bg-primary-50"
                                                        : "border-border"
                                                )}
                                            >
                                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                    <MapPin className="h-5 w-5 text-primary-600" />
                                                </div>
                                                <div className="flex-1 text-left">
                                                    <div className="font-medium text-foreground">
                                                        {location.name}
                                                    </div>
                                                    {location.city && (
                                                        <div className="text-sm text-muted-foreground">
                                                            {location.city}
                                                            {location.state && `, ${location.state}`}
                                                        </div>
                                                    )}
                                                </div>
                                                {typeof location.availableQuantity === 'number' && (
                                                    <span className="flex-shrink-0 text-xs text-success bg-success-subtle px-2 py-1 rounded-full font-medium">
                                                        {location.availableQuantity} available
                                                    </span>
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>

                                {/* Cancel Button */}
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    className="w-full"
                                >
                                    Keep browsing
                                </Button>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
